import type { ConcessionCardData } from '@/hooks/patients/useGetPatientConcessionCard';
import type { Consultation } from '@/hooks/patients/usePatientConsultations';
import type { GetPatientDetailsDto } from '@montugroup/pms-api-contracts';
import { useEffect, useState } from 'react';

export interface PatientDetailsContent {
  doctor: {
    id: number;
    prescriber_number: string;
    DoctorUser: {
      first_name: string;
      last_name: string;
      active: boolean;
      email: string;
      phone: string;
      clinic?: string;
      address?: string;
    };
  };
  patient: {
    id: number;
    patient_code: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    zip_code: string;
    dob: string;
    medicare_number: string;
    address: string;
    hasValidConcessionCard: boolean;
    allergies: string;
    state_id?: number;
  };
  consultations: Consultation[];
  isVersion2Prescription: boolean;
  showConsentFormResendLink: boolean;
}

export const usePatientDetailsContent = ({
  patientId,
  patientDetailsData,
  patientConsultationsData,
  concessionCardData,
}: {
  patientId?: number;
  patientDetailsData?: GetPatientDetailsDto;
  patientConsultationsData?: Consultation[];
  concessionCardData: ConcessionCardData | null;
}): { contents: PatientDetailsContent; setContents: (contents: PatientDetailsContent) => void } => {
  const [contents, setContents] = useState<PatientDetailsContent>({
    doctor: {
      id: 0,
      prescriber_number: '',
      DoctorUser: {
        first_name: '',
        last_name: '',
        active: false,
        email: '',
        phone: '',
        clinic: '',
        address: '',
      },
    },
    patient: {
      id: 0,
      patient_code: '',
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      zip_code: '',
      dob: '',
      medicare_number: '',
      address: '',
      hasValidConcessionCard: false,
      allergies: '',
      state_id: undefined,
    },
    consultations: [],
    isVersion2Prescription: false,
    showConsentFormResendLink: false,
  });

  useEffect(() => {
    if (!patientDetailsData && !patientConsultationsData && !concessionCardData) {
      return;
    }

    const basicInfo = patientDetailsData?.basicInfo?.attributes;
    const careInfo = patientDetailsData?.careInfo?.attributes;
    const identificationInfo = patientDetailsData?.identificationInfo?.attributes;
    const contactDetails = patientDetailsData?.contactDetails?.attributes;

    setContents((current) => ({
      ...current,
      doctor: {
        id: careInfo?.doctorId ?? 0,
        prescriber_number: careInfo?.doctor?.prescriberNumber ?? '',
        DoctorUser: {
          first_name: careInfo?.doctor?.firstName ?? '',
          last_name: careInfo?.doctor?.lastName ?? '',
          active: careInfo?.doctor?.active ?? false,
          email: careInfo?.doctor?.email ?? '',
          phone: careInfo?.doctor?.phone ?? '',
        },
      },
      patient: {
        ...current.patient,
        id: patientId ?? 0,
        patient_code: basicInfo?.patientCode ?? '',
        first_name: basicInfo?.firstName ?? '',
        last_name: basicInfo?.lastName ?? '',
        email: contactDetails?.email ?? '',
        phone: contactDetails?.phone ?? '',
        zip_code: basicInfo?.residentialAddress?.zipCode ?? '',
        dob: basicInfo?.dob ?? '',
        medicare_number: identificationInfo?.medicareNumber ?? '',
        address: basicInfo?.residentialAddress?.address ?? '',
        hasValidConcessionCard: concessionCardData?.hasValidConcessionCard ?? false,
        allergies: patientDetailsData?.medicalInfo?.attributes?.allergies ?? '',
        state_id: basicInfo?.residentialAddress?.state?.id ?? undefined,
      },
      consultations: patientConsultationsData ?? [],
      showConsentFormResendLink: basicInfo?.showConsentFormResend ?? false,
    }));
  }, [patientDetailsData, patientConsultationsData, patientId, concessionCardData?.hasValidConcessionCard]);

  return {
    contents,
    setContents,
  };
};
